import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { History, Search, Filter, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LogsPage = () => {
  const [data, setData] = useState({ logs: [], actors: [], actions: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ days: '30', user_id: 'all', action: 'all', q: '' });

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.days, filters.user_id, filters.action]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.days && filters.days !== 'all') params.set('days', filters.days);
      else params.set('days', '0');
      if (filters.user_id && filters.user_id !== 'all') params.set('user_id', filters.user_id);
      if (filters.action && filters.action !== 'all') params.set('action', filters.action);
      if (filters.q) params.set('q', filters.q);
      const { data } = await axios.get(`${BACKEND_URL}/api/logs/filtered?${params}`, {
        withCredentials: true,
      });
      setData(data);
    } catch (err) {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
  };

  const actionBadgeColor = (action) => {
    if (!action) return 'bg-slate-100 text-slate-700';
    if (action.startsWith('delete_')) return 'bg-rose-100 text-rose-700 border-rose-200';
    if (action.startsWith('create_') || action.startsWith('invite_')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (action.startsWith('update_') || action.startsWith('resend_')) return 'bg-sky-100 text-sky-700 border-sky-200';
    return 'bg-slate-100 text-slate-700';
  };

  const filteredLogs = useMemo(() => {
    if (!filters.q) return data.logs;
    const q = filters.q.toLowerCase();
    return data.logs.filter(
      (l) =>
        (l.user_name && l.user_name.toLowerCase().includes(q)) ||
        (l.user_email && l.user_email.toLowerCase().includes(q)) ||
        (l.action_label && l.action_label.toLowerCase().includes(q))
    );
  }, [filters.q, data.logs]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 flex items-center gap-3">
              <History className="h-7 w-7 text-indigo-600" />
              Logs de Auditoria
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Histórico completo de ações realizadas no sistema
            </p>
          </div>
          <button
            onClick={fetchLogs}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros
            </CardTitle>
            <CardDescription>{data.count} eventos encontrados</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSearchSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Período</Label>
                <Select value={filters.days} onValueChange={(v) => setFilters((p) => ({ ...p, days: v }))}>
                  <SelectTrigger data-testid="logs-filter-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Últimas 24h</SelectItem>
                    <SelectItem value="7">Últimos 7 dias</SelectItem>
                    <SelectItem value="30">Últimos 30 dias</SelectItem>
                    <SelectItem value="90">Últimos 90 dias</SelectItem>
                    <SelectItem value="all">Todo o histórico</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Usuário</Label>
                <Select value={filters.user_id} onValueChange={(v) => setFilters((p) => ({ ...p, user_id: v }))}>
                  <SelectTrigger data-testid="logs-filter-user">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os usuários</SelectItem>
                    {data.actors.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name || a.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Ação</Label>
                <Select value={filters.action} onValueChange={(v) => setFilters((p) => ({ ...p, action: v }))}>
                  <SelectTrigger data-testid="logs-filter-action">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as ações</SelectItem>
                    {data.actions.map((a) => (
                      <SelectItem key={a.value} value={a.value}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Busca</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Nome, e-mail, ação..."
                    value={filters.q}
                    onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                    className="pl-9"
                    data-testid="logs-search-input"
                  />
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Carregando...</div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-16">
                <History className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium mb-1">Nenhum log encontrado</p>
                <p className="text-sm text-slate-500">Ajuste os filtros para ver outros períodos.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Data/Hora</th>
                      <th className="py-3 px-4">Usuário</th>
                      <th className="py-3 px-4">Ação</th>
                      <th className="py-3 px-4">Recurso</th>
                      <th className="py-3 px-4">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log, idx) => (
                      <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`log-row-${idx}`}>
                        <td className="py-3 px-4 text-slate-700 tabular-nums whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="py-3 px-4">
                          {log.user_name ? (
                            <div>
                              <div className="font-medium text-slate-900">{log.user_name}</div>
                              <div className="text-xs text-slate-500">{log.user_email}</div>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs italic">Sistema</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={actionBadgeColor(log.action)}>
                            {log.action_label || log.action}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-700">
                            {log.resource_id ? String(log.resource_id).slice(0, 12) : '—'}
                          </code>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-500">
                          {Array.isArray(log.details) ? log.details.join(', ') : log.details || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
